import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";
import { loadLogoAsBase64 } from "./logoLoader";

pdfMake.vfs = pdfFonts.default;

export async function generateAgreementPdf(
  agreement,
  signers = [],
  report = {},
  attachments = []
) {
  if (!agreement) {
    console.error("No hay datos del convenio para generar el PDF");
    return;
  }

  const logoBase64 = await loadLogoAsBase64();

  const safeValue = (value, alt = "Pendiente") =>
    value && value !== "-" ? value : alt;

  const translateStatus = (status) => {
    switch (status) {
      case "executed":
        return { text: "Ejecutado", color: "green", bold: true };
      case "unexecuted":
        return { text: "No Ejecutado", color: "gray", bold: true };
      default:
        return { text: "Pendiente", color: "red", italics: true };
    }
  };

  const orderNumber = safeValue(agreement.order_number);
  const objective = safeValue(agreement.objective);
  const agreementType = safeValue(agreement.agreement_type);
  const entity = safeValue(agreement.entity);
  const resolutionNumber = safeValue(agreement.resolution_number);
  const suscriptionDate = agreement.suscription_date
    ? dayjs(agreement.suscription_date).format("DD/MM/YYYY")
    : "Pendiente";
  const effectiveDate = agreement.effective_date
    ? dayjs(agreement.effective_date).format("DD/MM/YYYY")
    : "Pendiente";

  const academicUnit = safeValue(report?.academic_unit);
  const career = safeValue(report?.career);
  const proposal = safeValue(report?.proposal);
  const evaluatedPeriod = safeValue(report?.evaluated_period);
  const evaluationUnit = safeValue(report?.evaluation_unit);
  const presentationDate = report?.presentation_date
    ? dayjs(report.presentation_date).format("DD/MM/YYYY")
    : "Pendiente";
  const observation = safeValue(report?.observation, "Sin observaciones");

  const coordinators = report?.coordinators || [];
  const indicators = report?.indicators || [];

  const docDefinition = {
    pageMargins: [40, 120, 40, 60],
    header: () => ({
      margin: [30, 20, 30, 10],
      table: {
        widths: [70, "*"],
        body: [
          [
            {
              image: logoBase64,
              width: 55,
              height: 65,
              alignment: "left",
              margin: [0, 0, 10, 0],
            },
            {
              stack: [
                {
                  text: "UNIVERSIDAD NACIONAL DE ITAPÚA",
                  style: "universityName",
                  alignment: "center",
                  margin: [0, 0, 0, 4],
                },
                {
                  text: "Información del Convenio",
                  style: "headerText",
                  alignment: "center",
                },
              ],
              alignment: "center",
            },
          ],
        ],
      },
      layout: "noBorders",
    }),

    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: "center",
      margin: [0, 20, 0, 0],
      fontSize: 9,
    }),

    content: [
      {
        table: {
          widths: ["35%", "65%"],
          body: [
            ["N° de orden", orderNumber],
            ["Objetivo", objective],
            ["Tipo de convenio", agreementType],
            ["Institución co-celebrante", entity],
            ["Nº de resolución", resolutionNumber],
            ["Fecha de suscripción", suscriptionDate],
            ["Fecha de vigencia", effectiveDate],
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 5, 0, 15],
      },

      { text: "Firmantes", style: "subtitle" },
      {
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              { text: "Nombre", bold: true },
              { text: "Cargo", bold: true },
            ],
            ...(signers.length
              ? signers.map((s) => [safeValue(s.name), safeValue(s.position)])
              : [["Pendiente", "Pendiente"]]),
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 0, 0, 20],
      },

      { text: "Análisis y Dictamen", style: "subtitle" },
      {
        table: {
          widths: ["35%", "65%"],
          body: [
            ["Unidad académica", academicUnit],
            ["Carrera", career],
            ["Celebrado a propuesta de", proposal],
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 0, 0, 15],
      },

      { text: "Coordinadores", style: "subtitle" },
      {
        ul: coordinators.length
          ? coordinators.map((c) => c.name || "Pendiente")
          : ["Pendiente"],
        margin: [0, 0, 0, 20],
      },

      // 🔹 Indicadores solo si el convenio es específico
      ...(agreementType.toLowerCase() === "específico"
        ? [
            { text: "Indicadores", style: "subtitle" },
            indicators.length
              ? {
                  table: {
                    widths: ["30%", "30%", "40%"],
                    body: [
                      [
                        { text: "Nombre", bold: true },
                        { text: "Estado", bold: true },
                        { text: "Descripción", bold: true },
                      ],
                      ...indicators.map((ind) => [
                        safeValue(ind.name),
                        translateStatus(ind.status),
                        safeValue(ind.description),
                      ]),
                    ],
                  },
                  layout: "lightHorizontalLines",
                  margin: [0, 0, 0, 20],
                }
              : {
                  text: "No hay indicadores registrados",
                  italics: true,
                  margin: [0, 0, 0, 20],
                },
          ]
        : []),

      { text: "Evaluación", style: "subtitle" },
      {
        table: {
          widths: ["40%", "60%"],
          body: [
            ["Período evaluado", evaluatedPeriod],
            ["Dependencia evaluadora", evaluationUnit],
            ["Fecha de presentación", presentationDate],
            ["Observación", observation],
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 0, 0, 20],
      },

      { text: "Archivos adjuntos", style: "subtitle" },
      agreement.images && agreement.images.length
        ? {
            ul: agreement.images.map((img) => ({
              text: img.fileName || "Archivo",
              link: img.uri,
              color: "blue",
              decoration: "underline",
            })),
            margin: [0, 0, 0, 20],
          }
        : {
            text: "No hay archivos adjuntos",
            italics: true,
            margin: [0, 0, 0, 20],
          },
    ],

    styles: {
      header: { fontSize: 14, bold: true },
      subheader: { fontSize: 10, italics: true },
      title: { fontSize: 16, bold: true, margin: [0, 10, 0, 10] },
      subtitle: { fontSize: 13, bold: true, margin: [0, 10, 0, 5] },
    },
  };

  pdfMake.createPdf(docDefinition).open();
}
